import MicIcon from '@mui/icons-material/MicNone';
import StopIcon from '@mui/icons-material/StopCircleOutlined';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import FormatTime from 'components/FormatTime';
import { events } from 'services/analytics';
import { decodeAudioBlob } from 'pages/Remixer/audioHelpers';
import { MicrophoneContext } from '../MicrophoneProvider';
import {
  StyledRecordButton,
  StyledRecorder,
  StyledStopButton,
  StyledTime,
} from './styled';

const MAX_RECORDING_TIME = 30;

interface RecorderProps {
  onBlobRecorded: (blob: Blob) => void;
}

const Recorder = ({ onBlobRecorded }: RecorderProps) => {
  const { track } = useAnalytics();
  const { mediaStream } = useContext(MicrophoneContext);

  const [isRecording, setIsRecording] = useState(false);
  const [time, setTime] = useState(0);

  const handleDataAvailable = useCallback(
    async ({ data: blob }: BlobEvent) => {
      onBlobRecorded(blob);
      const { duration } = await decodeAudioBlob(blob);
      track(events.audioClipRecorded({ duration }));
    },
    [onBlobRecorded, track]
  );

  const recorder = useMemo(() => {
    if (!mediaStream) {
      return null;
    }
    return new MediaRecorder(mediaStream);
  }, [mediaStream]);

  useEffect(() => {
    recorder?.addEventListener('dataavailable', handleDataAvailable);

    return () => {
      recorder?.removeEventListener('dataavailable', handleDataAvailable);
    };
  }, [recorder, handleDataAvailable]);

  useEffect(() => {
    let interval: number;

    const handleStart = () => {
      setTime(0);
      setIsRecording(() => true);
      interval = setInterval(
        () => setTime((current) => current + 1),
        1000
      ) as unknown as number;
    };

    const handleStop = () => {
      setIsRecording(() => false);
      clearInterval(interval);
    };

    recorder?.addEventListener('start', handleStart);
    recorder?.addEventListener('stop', handleStop);

    return () => {
      recorder?.removeEventListener('start', handleStart);
      recorder?.removeEventListener('stop', handleStop);
    };
  }, [recorder]);

  const handleStartRecording = useCallback(() => recorder?.start(), [recorder]);
  const handleStopRecording = useCallback(() => recorder?.stop(), [recorder]);

  useEffect(() => {
    if (time >= MAX_RECORDING_TIME) {
      handleStopRecording();
    }
  }, [time, handleStopRecording]);

  return (
    <StyledRecorder>
      {isRecording ? (
        <StyledStopButton onClick={handleStopRecording}>
          <StopIcon />
        </StyledStopButton>
      ) : (
        <StyledRecordButton
          onClick={handleStartRecording}
          disabled={!mediaStream}
        >
          <MicIcon />
        </StyledRecordButton>
      )}
      {mediaStream && (
        <StyledTime>
          <FormatTime time={time} />
        </StyledTime>
      )}
    </StyledRecorder>
  );
};

export default Recorder;
